/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useRollbar, useRollbarPerson } from '@rollbar/react';

import Swal from 'sweetalert2';
import Tab from 'react-bootstrap/Tab';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import LinearProgress from '@mui/material/LinearProgress';

import apiService from '@services/apiService';

import Button from '@components/smartAdminUI/Button';
import Panel from '@components/smartAdminUI/Panel';
import FormSearchBar from '@components/form/formComponents/formSearch/FormSearchBar';
import FormLoader from '@components/loaders/FormLoader';

import AgentLookupModal from '@components/form/formComponents/inputGroups/agentLookup/AgentLookupModal';
import FieldValidator from '@components/form/formHelpers/FieldValidator';
import FormikInputGroup from '@components/smartAdminUI/input/FormikInputGroup';
import FormikFieldUpdater from '@components/form/formHelpers/FormikFieldUpdater';
import LayoutGroupWrapper from '@components/form/formComponents/inputGroups/LayoutGroupWrapper';
import OfficeLookupModal from '@components/form/formComponents/inputGroups/officeLookup/OfficeLookupModal';
import RepeatableRoom from '@components/form/formComponents/repeatableRoom/RepeatableRoom';
import OnSubmitValidationError from '@components/form/formikComponents/OnSubmitValidationError';

import GAEventTracking from '@components/tracking/GAEventTracking';

// import Debug from '@components/general/Debug';

import {
  getSortedErrors,
  selectComboValueDict,
  selectFormDefinition,
  selectInitialTouched,
  selectInitialValues,
  selectLookupNames,
  selectVisiblePageStructure,
  selectAllFields,
  getOrderedSections,
  selectRequiredPerSection,
  selectErrorsPerSection,
} from '@redux/selectors/FormSelector';

import { selectUploadedImages, selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';
import { getUserName, getUserMlsId } from '@redux/selectors/UserSelector';
import { selectOfficeFormData } from '@redux/selectors/FormDataSelector';

import {
  formSubmitted,
  loadComboBoxOptions,
  setValidationResult,
} from '@redux/modules/formGeneration';

import { clearOffice } from '@redux/modules/office';
import SectionNav from '@components/smartAdminUI/SectionNav';

import useRowLock from '@hooks/useRowLock';
import usePrevious from '@hooks/usePrevious';
import useIsMounted from '@hooks/useIsMounted';
import FormikField from '@components/form/FormikField';

const CreateOfficeForm = () => {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const formRef = useRef();
  const history = useHistory(); // use for redirection

  const officeRules = window.FlexRule.RuntimeEngine.fromJavaScript('OnePlatform', 'OfficeRules');

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const reqPerSection = useSelector(selectRequiredPerSection);
  const orderedSections = useSelector(getOrderedSections);
  const errPerSection = useSelector(selectErrorsPerSection);
  const agentLookup = useSelector(state => state.agent.agentLookup);
  const definitionType = useSelector(state => state.formGeneration.definitionType);
  const formIsLoading = useSelector(state => state.formGeneration.formIsLoading);
  const officeLookup = useSelector(state => state.office.officeLookup);
  const officeSelected = useSelector(state => state.office.currentOffice);
  const officeFormData = useSelector(selectOfficeFormData);
  const operationType = useSelector(state => state.formGeneration.operationType);
  const comboSetValueDict = useSelector(selectComboValueDict);
  const currentFormDefinition = useSelector(selectFormDefinition);
  const lookUpNames = useSelector(selectLookupNames);
  const initialTouched = useSelector(selectInitialTouched);
  const initialValues = useSelector(selectInitialValues);
  const pageStructure = useSelector(selectVisiblePageStructure);
  const allFields = useSelector(selectAllFields);
  const uploadedImages = useSelector(selectUploadedImages);
  const sortedErrors = useSelector(getSortedErrors);

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [newComboValue, setNewComboValue] = useState({});
  const [runValidation, setRunValidation] = useState(false);
  const [validateFieldName, setValidateFieldName] = useState('');
  const [validateFlag, setValidateFlag] = useState(false);
  const [validationValue, setValidationValue] = useState('');
  const [progress, setProgress] = useState(((1 / orderedSections.length) * 100));
  const [activeSection, setActiveSection] = useState(1);

  const gaEventTracker = GAEventTracking('Error Office Form Submit');
  const username = useSelector(getUserName);
  const userMLSID = useSelector(getUserMlsId);

  const rollbar = useRollbar();
  useRollbarPerson({ id: userMLSID, username });

  const previousOperationType = usePrevious(operationType);

  const memoizedInitialValues = useMemo(() => initialValues, []);
  const rowLockEntity = useMemo(() => ({ type: 'office', id: officeSelected?.UniqueID }), [officeSelected]);

  const comboBubbleWrap = { setNewComboValue };

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    checked,
    checkStatus,
    createLock,
    removeLock,
    rowLockingEnabled,
  } = useRowLock({ rowLockEntity, isEditOperation: operationType === 'edit', enableRowLocking: operationType === 'edit' });

  const lockReleasedByCurrentUser = useRef(false);

  useEffect(() => {
    if (!currentFormDefinition?.length) return;

    if (lookUpNames?.lookupNames?.length) {
      dispatch(loadComboBoxOptions(lookUpNames, 'offices'));
    }

    formRef?.current?.validateForm();
  }, [currentFormDefinition]);

  useEffect(() => {
    // prevent the execution of this effect when going from edit mode to create mode;
    // current component will not be un-mounted yet when [operationType] gets updated in redux
    if (previousOperationType === 'edit' && operationType === 'create') return;

    if (operationType === 'create') {
      dispatch(clearOffice());
      formRef?.current?.resetForm();
      setRunValidation(true);
    }
  }, [operationType]);

  useEffect(() => {
    if (Object.keys(newComboValue).length > 0) {
      const newComboKeys = Object.keys(newComboValue);
      if (comboSetValueDict) {
        if (newComboKeys.length > 0) {
          const { fieldName, newValue } = newComboValue;
          const comboMutator = comboSetValueDict[fieldName];
          comboMutator(newValue);
        }
      }
    }
  }, [newComboValue]);

  const getDeltas = (formikState, unmodifiedEntry) => {
    const deltas = {};
    if (!formikState) return deltas;
    Object.entries(unmodifiedEntry).map(([key, value]) => {
      if (key in formikState && formikState?.[key] !== value) {
        deltas[key] = formikState?.[key];
      }
      return null;
    });
    return deltas;
  };

  const mergePhotoData = (formikState, uploadList = []) => {
    const mergedPhotos = uploadList.flatMap(images => {
      if (!images) return [];
      return images.map(singleImage => {
        const currentDisplayName = singleImage.displayName ?? '';
        const imageKey = singleImage?.key ?? singleImage?.UniqueID ?? singleImage?.displayName;

        if (`${currentDisplayName}`.includes('_thumbnail')) return null;
        if (singleImage.UniqueID) {
          // Handle existing photo.
          const { SortOrder, IsDeleted } = formikState[imageKey];
          if (IsDeleted) {
            // Mark photo as deleted.
            return {
              UniqueID: singleImage.UniqueID,
              SortOrder: singleImage.SortOrder,
              IsDeleted,
            };
          }
          // Update photo.
          const deltas = getDeltas(formikState[imageKey], singleImage);
          return {
            ...deltas,
            UniqueID: singleImage.UniqueID,
            SortOrder,
            IsDeleted,
          };
        }
        // Handle new photo.
        const {
          URL,
          Description,
          SortOrder,
          IsDeleted,
        } = formikState[imageKey];
        if (IsDeleted) return null;
        return {
          URL,
          Description,
          SortOrder,
          IsDeleted,
        };
      }).filter(photo => photo !== null);
    });
    return mergedPhotos;
  };

  const convertDateToUTC = date => new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());

  const getDateValue = value => {
    let formattedDate;
    if (value) {
      const dateUTC = convertDateToUTC(new Date(value));
      const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(dateUTC);
      const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(dateUTC);
      const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(dateUTC);
      formattedDate = `${year}-${month}-${day}`;
    }
    return formattedDate ?? '';
  };

  const getBitValue = value => {
    let bitValue;
    if (value != null && value !== '') {
      if (typeof value === 'string') {
        bitValue = value === 'true' ? 1 : 0;
      } else if (typeof value === 'boolean') {
        bitValue = value === true ? 1 : 0;
      } else if (typeof value === 'number') {
        bitValue = value;
      }
    }
    return bitValue ?? '';
  };

  const getStringValue = value => value ?? '';

  const getNumericValue = value => (value != null && value !== '' ? Number(value) : '');

  const getComboBoxValue = value => value ?? '';

  // TODO: move to utility module (used by listing/agent/office)
  const valueHasChanged = (fieldName, oldValue, newValue) => {
    const inputType = allFields[fieldName]?.InputType;
    const dataType = allFields[fieldName]?.DataType;
    switch (inputType) {
      case 'TextInput':
        if (dataType === 'datetime' || dataType === 'date') {
          return (getDateValue(oldValue) !== getDateValue(newValue));
        }
        if (dataType === 'bit') {
          return (getBitValue(oldValue) !== getBitValue(newValue));
        }
        if (dataType === 'string') {
          return (getStringValue(oldValue) !== getStringValue(newValue));
        }
        if (dataType === 'numeric' || dataType === 'decimal' || dataType === 'integer') {
          return (getNumericValue(oldValue) !== getNumericValue(newValue));
        }
        return (oldValue !== newValue);

      case 'ComboBox':
        return (getComboBoxValue(oldValue) !== getComboBoxValue(newValue));

      default:
        return (oldValue !== newValue);
    }
  };

  const handlePreviousClick = () => {
    const tabNav = document.querySelector('.tab-nav');
    const tabActive = tabNav.querySelector('.active');
    if (tabActive) {
      const navItem = tabActive.parentNode;
      const previous = navItem.previousElementSibling;
      if (previous) {
        previous.querySelector('a').click();
        window.scrollTo(0, 0);
        setProgress(progress - ((1 / orderedSections.length) * 100));
        setActiveSection(activeSection - 1);
      }
    }
  };

  const handleNextClick = () => {
    const tabNav = document.querySelector('.tab-nav');
    const tabActive = tabNav.querySelector('.active');
    if (tabActive) {
      const navItem = tabActive.parentNode;
      const next = navItem.nextElementSibling;
      if (next) {
        next.querySelector('a').click();
        window.scrollTo(0, 0);
        setProgress(progress + ((1 / orderedSections.length) * 100));
        setActiveSection(activeSection + 1);
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!(operationType === 'create') && !(operationType === 'edit')) return;

    setFormSubmitting(true);
    const { Photos, ...office } = values;

    const photosGroup = Photos
      ? mergePhotoData(Photos, uploadedImages)
      : null;

    // CREATE ONLY
    const postBody = {
      photos: photosGroup,
    };

    if (operationType === 'create') {
      postBody.office = { ...office };
    } else if (operationType === 'edit') {
      const officeData = {
        UniqueID: officeSelected.UniqueID,
      };

      Object.keys(office).map(key => {
        if (valueHasChanged(key, officeSelected[key], values[key])) {
          officeData[key] = values[key];
        }
        return null;
      });

      postBody.office = { ...officeData };
    }

    let uniqueID;
    try {
      // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
      if (!isMounted) return;

      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        icon: 'question',
        iconColor: 'var(--theme-warning)',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-warning btn-lg mr-2 swal-question-office-submit-yes',
          denyButton: 'btn btn-primary btn-lg swal-question-office-submit-no',
        },
        confirmButtonText: 'Yes, Continue',
        denyButtonText: 'No, Cancel',
        showDenyButton: true,
        title: 'Submit Office?',
        text: 'Your changes will be reflected in Matrix™ within 10-15 minutes.',
      }).then(async choice => {
        if (choice.isConfirmed) {
          if (operationType === 'create') {
            const result = await apiService.Office.insertOffice(postBody);
            uniqueID = result?.data?.UniqueID;
          } else if (operationType === 'edit') {
            uniqueID = officeSelected?.UniqueID;
            await apiService.Office.updateOffice(postBody);
          }

          dispatch(formSubmitted());
          removeLock();
          lockReleasedByCurrentUser.current = true;

          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Office Submitted',
            icon: 'success',
            iconColor: 'var(--theme-success)',
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            history.push(uniqueID == null ? '/' : `/office/view?UniqueID=${uniqueID}`);
          });
        }
      });
    } catch (error) {
      // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
      if (!isMounted) return;

      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Office Not Submitted',
        text: 'Office could not be submitted at this time. Please contact support.',
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-office-submit',
        },
      });
      if (operationType === 'edit') gaEventTracker(`Edit - ${uniqueID}`, `${username}`);
      if (operationType === 'create') gaEventTracker(`Create - ${uniqueID}`, `${username}`);
      rollbar.error('Error: Office Form Submit', error, postBody);
    }
    setFormSubmitting(false);
    setSubmitting(false);
  };

  const onClickSubmit = (values, actions) => {
    if (rowLockingEnabled) return checkStatus(async () => handleSubmit(values, actions)); // check lock status before submitting
    return handleSubmit(values, actions);
  };

  const transformRepeatable = values => {
    const filledValues = { ...values };
    const existingLogics = officeRules.descriptions.logics; // existing fields that have validation rules
    const newValues = {};

    Object.entries(filledValues).forEach(([key, value]) => { // now handles removal of fields that do not have the validation logic set
      const newKeyLogic = `${key}Rules`;
      if (existingLogics.includes(newKeyLogic)) {
        newValues[key] = value;
      }
    });
    return { office: newValues };
  };

  const myValidateForm = values => {
    const errors = {};

    if (officeRules) {
      try {
        const { office, photos } = transformRepeatable(values);

        const res = officeRules.run(office, photos);

        const { fieldValidationResult, officeIsValid } = res.context.variableContainer;

        dispatch(setValidationResult(fieldValidationResult));

        if (!officeIsValid) {
          errors.officeIsValid = officeIsValid;
        }
      } catch (er) {
        rollbar.error('Error: Office Validation', er, values);
      }
    }
    return errors;
  };

  const validateFigField = (figFieldName, figValidationValue) => {
    setValidateFieldName(figFieldName);
    setValidateFlag(true);
    setValidationValue(figValidationValue);
  };

  const fieldsUpdated = () => {
    setRunValidation(true);
  };

  useEffect(() => {
    if (runValidation) {
      formRef?.current?.validateForm();
      setRunValidation(false);
    }
  }, [runValidation]);

  // prevents form from submitting on enter key press
  const handleEnterKeyDown = keyEvent => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const handleDiscardChanges = e => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      title: 'Discard Changes?',
      html: 'Your changes will be discarded.',
      iconHtml: '<i class="far fa-trash-alt fa-xs"/>',
      iconColor: 'var(--theme-danger)',
      buttonsStyling: false,
      showDenyButton: true,
      confirmButtonText: 'Yes, Continue',
      denyButtonText: 'No, Cancel',
      customClass: {
        confirmButton: 'btn btn-danger btn-lg mr-2 swal-question-office-discard-yes',
        denyButton: 'btn btn-primary btn-lg swal-question-office-discard-no',
      },
    }).then(result => {
      if (result.isConfirmed) {
        if (operationType) {
          e.preventDefault();
          removeLock();
          lockReleasedByCurrentUser.current = true;
          history.push(operationType === 'create' ? '/' : '/office/view');
        }
      }
    });
  };

  // updates row lock and displays an alert when changes are being overwritten by another user
  useEffect(() => {
    const tryRegainRowLock = async () => {
      try {
        const created = await createLock();
        if (!created) {
          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Lock Lost',
            text: 'Your lock was lost and could not be regained.',
            iconHtml: '<i class="far fa-lock-open fa-xs"/>',
            iconColor: 'var(--theme-warning)',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-warning btn-lg swal-warning-office-lockLost',
            },
          }).then(resp => {
            if (resp.value) {
              history.push('/office/view');
            }
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error: Office tryRegainRowLock', err);
      }
    };

    if (operationType === 'edit' && !lockReleasedByCurrentUser.current) {
      if (checked && !locked) {
        tryRegainRowLock();
        return;
      }

      if (locked && !lockingAuthorized) {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Changes Overwritten',
          text: `Your changes have been overwritten by ${lockedBy}.`,
          iconHtml: '<i class="far fa-lock-open fa-xs"/>',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-warning-office-changesOverwritten',
          },
        }).then(resp => {
          if (resp.value) {
            history.push('/office/view');
          }
        });
      }
    }
  }, [locked, lockingAuthorized, lockedBy, operationType]);

  const datePickerWhitelist = ['datetime', 'date', 'time'];
  const requiresDatePicker = childDataType => datePickerWhitelist.includes(childDataType);

  return (
    <>
      <AgentLookupModal />
      <OfficeLookupModal />
      <Formik
        innerRef={formRef}
        initialValues={memoizedInitialValues}
        initialTouched={initialTouched}
        onSubmit={onClickSubmit}
        validate={myValidateForm}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {() => (
          <>
            <OnSubmitValidationError
              onSubmissionError={() => {
                if (sortedErrors?.length > 0) {
                  Swal.fire({
                    allowEnterKey: false,
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    position: 'center',
                    title: 'Validation Failed',
                    html: `Please correct <strong class="text-danger">${sortedErrors.length}</strong> validation errors and try again.`,
                    confirmButtonText: 'Ok',
                    icon: 'error',
                    iconColor: 'var(--theme-danger)',
                    buttonsStyling: false,
                    customClass: {
                      confirmButton: 'btn btn-danger btn-lg swal-fail-office-validation',
                    },
                  });
                }
              }}
            />
            <Form onKeyDown={handleEnterKeyDown}>
              <Panel
                customClass={formIsLoading ? 'sticky' : 'd-none'}
                header={(
                  <>
                    <i className="far fa-briefcase mr-2" />
                    {officeFormData?.OfficeMlsId ? officeFormData.OfficeMlsId : 'Office'}
                  </>
                )}
              >
                <FormLoader />
              </Panel>
              <Panel
                customClass={formIsLoading ? 'd-none' : 'sticky'}
                header={(
                  <>
                    <i className="far fa-briefcase mr-2" />
                    {officeFormData?.OfficeMlsId ? officeFormData.OfficeMlsId : 'Office'}
                  </>
                )}
                panelToolbar={(
                  <>
                    <Button
                      name="btnDiscardChangesOffice"
                      variant="danger btn-sm d-none d-sm-block"
                      onClick={handleDiscardChanges}
                    >
                      <i className="fas fa-trash-alt mr-2" aria-hidden="true" />
                      Discard Changes
                    </Button>
                    <Button
                      name="btnDiscardChangesOffice"
                      variant="danger btn-sm d-block d-sm-none"
                      onClick={handleDiscardChanges}
                      tooltipText="Discard Changes"
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </Button>
                    <Button
                      name="btnSubmitOffice"
                      variant="success btn-sm d-none d-sm-block"
                      type="submit"
                    >
                      {formSubmitting
                        ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="far fa-chevron-circle-right mr-2" aria-hidden="true" />
                            Submit
                          </>
                        )}
                    </Button>
                    <Button
                      name="btnSubmitOffice"
                      variant="success btn-sm d-block d-sm-none"
                      type="submit"
                      tooltipText="Submit"
                    >
                      {formSubmitting ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <i className="fas fa-save" aria-hidden="true" />}
                    </Button>
                  </>
                )}
                footer={(
                  <>
                    <div className="p-3 w-75">
                      <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
                    </div>
                    <div className="pr-2 w-25 d-flex">
                      <Button
                        name="btnPreviousSectionOffice"
                        className="btn-sm d-none d-lg-block ml-auto"
                        onClick={handlePreviousClick}
                        disabled={activeSection === 1}
                      >
                        <i className="fas fa-chevron-left mr-2" />
                        Previous
                      </Button>
                      <Button
                        name="btnPreviousSectionOffice"
                        className="btn-sm d-block d-lg-none ml-auto"
                        onClick={handlePreviousClick}
                        tooltipText="Previous"
                        disabled={activeSection === 1}
                      >
                        <i className="fas fa-chevron-left" />
                      </Button>
                      <Button
                        name="btnNextSectionOffice"
                        className="btn-sm d-none d-lg-block"
                        onClick={handleNextClick}
                        disabled={activeSection === orderedSections.length}
                      >
                        <i className="fas fa-chevron-right mr-2" />
                        Next
                      </Button>
                      <Button
                        name="btnNextSectionOffice"
                        className="btn-sm d-block d-lg-none"
                        onClick={handleNextClick}
                        tooltipText="Next"
                        disabled={activeSection === orderedSections.length}
                      >
                        <i className="fas fa-chevron-right" />
                      </Button>
                    </div>
                  </>
                )}
              >
                <Tab.Container
                  id="sections"
                  defaultActiveKey={orderedSections[activeSection - 1]?.sectionName}
                  onSelect={key => {
                    setProgress((1 / orderedSections.length) * 100 * (orderedSections.indexOf(orderedSections.find(x => x.sectionName === key)) + 1));
                    setActiveSection(orderedSections.indexOf(orderedSections.find(x => x.sectionName === key)) + 1);
                  }}
                >
                  <Row>
                    <Col xl={4}>
                      <SectionNav
                        sectionsPerRepeatableSection={sectionsPerRepeatableSection}
                        sections={orderedSections}
                        errPerSection={errPerSection}
                        reqPerSection={reqPerSection}
                      />
                    </Col>
                    <Col xl={8}>
                      <FormSearchBar />
                      <Tab.Content className="pb-6">
                        {pageStructure.map(section => (
                          <Tab.Pane
                            eventKey={section.sectionName.replace(/\s/g, '')}
                            key={section.sectionName.replace(/\s/g, '')}
                          >
                            {section.sectionName !== 'Rooms' ? (
                              <>
                                <h3 className="fs-xl mb-4">{section.sectionLabel}</h3>
                                {section.sectionName !== 'Rooms' && section.children.map(child => {
                                  if (child.isGroup) {
                                    return (
                                      <LayoutGroupWrapper
                                        key={child.groupName}
                                        validateFigField={validateFigField}
                                        comboBubbleWrap={comboBubbleWrap}
                                        {...child}
                                      />
                                    );
                                  }
                                  if (requiresDatePicker(child.datatype)) {
                                    return (
                                      <FormikField
                                        key={child.RowID}
                                        name={child.FieldName}
                                        validateFigField={validateFigField}
                                        {...child}
                                      />
                                    );
                                  }
                                  return (
                                    <FormikInputGroup
                                      key={child.RowID}
                                      validateFigField={validateFigField}
                                      comboBubbleWrap={comboBubbleWrap}
                                      {...child}
                                    />
                                  );
                                })}
                              </>
                            ) : (
                              <RepeatableRoom validateFigField={validateFigField} />
                            )}
                          </Tab.Pane>
                        ))}
                        {/* <Debug /> */}
                        <FieldValidator
                          validationRules={officeRules}
                          validateFieldName={validateFieldName}
                          validateFlag={validateFlag}
                          validationValue={validationValue}
                          setValidateFlag={setValidateFlag}
                          setValidationValue={setValidationValue}
                          setValidateFieldName={setValidateFieldName}
                        />
                        <FormikFieldUpdater
                          definitionType={definitionType}
                          officeLookup={officeLookup}
                          officeManagerLookup={agentLookup}
                          officeFormData={officeFormData}
                          runValidation={fieldsUpdated}
                        />
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Panel>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default CreateOfficeForm;
